import { compareDesc } from 'date-fns/compareDesc';
import { useViewType, useViewportId } from '../state';
import { createActiveSliceChangedEmitterEvent } from './BaseImagingProvider';
import { useImagingContext } from './ImagingContext';

import { useEffect, useMemo } from 'react';
import {
  atomFamily,
  selectorFamily,
  useRecoilCallback,
  useRecoilValue,
  useRecoilValueLoadable,
  useResetRecoilState,
} from 'recoil';
import type { RecoilState, RecoilValueReadOnly } from 'recoil';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useStudies, useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { REVIEWED_THRESHOLD } from 'config/constants';
import type { Stack, Study } from 'domains/viewer/ViewportsConfigurations/types';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import { DEFAULT_STUDY_DATE_VALUE } from 'domains/reporter/RichTextEditor/plugins/deepLink/constants';
import { WithApolloContext } from 'modules/Apollo/types';

export type CaseReviewedStatus = {
  seriesSmid: string;
  studySmid: string;
  studyDate: Nullable<Date>;
  studyDescription: Nullable<string>;
  threshold: number;
};

export type ReviewedStudyByDate = {
  smid: string;
  description: Nullable<string>;
  studyDate: Nullable<Date>;
};

const EMPTY_REVIEWED_STUDIES_BY_DATE: Map<string, ReviewedStudyByDate> = new Map();

export function caseReviewedStatusesMergeFunction(
  currentReviewedStatuses: Nullable<CaseReviewedStatus[]>,
  incomingReviewedStatuses: CaseReviewedStatus[]
): CaseReviewedStatus[] {
  if (currentReviewedStatuses == null) return incomingReviewedStatuses;

  const newStatuses: Array<CaseReviewedStatus> = [];
  incomingReviewedStatuses.forEach((incomingStatus) => {
    if (
      currentReviewedStatuses.every(
        (currentStatus) => currentStatus.seriesSmid !== incomingStatus.seriesSmid
      )
    ) {
      newStatuses.push(incomingStatus);
    }
  });

  if (newStatuses.length === 0) return currentReviewedStatuses;

  return [...currentReviewedStatuses, ...newStatuses];
}

export const caseReviewedStatuses: (caseSmid: string) => RecoilState<CaseReviewedStatus[]> =
  atomFamily({
    key: 'caseReviewedStatuses',
    default: [],
    effects: [
      broadcastChannelSynchronizerEffect({
        mergeFn: caseReviewedStatusesMergeFunction,
      }),
      // @ts-expect-error [EN-10263] - TS2322 - Type 'AtomEffect<never[]>' is not assignable to type 'AtomEffect<CaseReviewedStatus[]>'.
      localStoragePersisterEffect({ version: 1 }),
    ],
  });

export const isStackReviewed: (arg1: {
  caseSmid: Nullable<string>;
  stackSeriesSmids: Array<string>;
}) => RecoilValueReadOnly<boolean> = selectorFamily({
  key: 'isStackReviewed',
  get:
    ({ caseSmid, stackSeriesSmids }) =>
    ({ get }) => {
      if (caseSmid == null || stackSeriesSmids.length === 0) return false;

      const reviewedStatuses = get(caseReviewedStatuses(caseSmid));
      return reviewedStatuses.some((reviewedStatus) =>
        stackSeriesSmids.some((stackSeriesSmid) => reviewedStatus.seriesSmid === stackSeriesSmid)
      );
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const reviewedStudySmids: (caseSmid?: Nullable<string>) => RecoilValueReadOnly<Set<string>> =
  selectorFamily({
    key: 'reviewedStudies',
    get:
      (caseSmid) =>
      ({ get }) => {
        if (caseSmid == null) return new Set();

        const reviewedStatuses = get(caseReviewedStatuses(caseSmid));
        return new Set(reviewedStatuses.map((reviewedStatus) => reviewedStatus.studySmid));
      },
    cachePolicy_UNSTABLE: {
      eviction: 'most-recent',
    },
  });

export const reviewedStudiesByDateMap: (arg1: {
  caseSmid: Nullable<string>;
  currentStudySmids: ReadonlyArray<string>;
}) => RecoilValueReadOnly<Map<string, ReviewedStudyByDate>> = selectorFamily({
  key: 'reviewedStudiesByDate',
  get:
    ({ caseSmid, currentStudySmids }) =>
    ({ get }) => {
      if (caseSmid == null || currentStudySmids == null) return new Map();

      const allReviewedStatuses = [...get(caseReviewedStatuses(caseSmid))];
      const reviewedStatuses = allReviewedStatuses.filter(
        (study) => !currentStudySmids.includes(study.studySmid)
      );
      reviewedStatuses.sort((a, b) =>
        compareDesc(
          new Date(a.studyDate ?? DEFAULT_STUDY_DATE_VALUE),
          new Date(b.studyDate ?? DEFAULT_STUDY_DATE_VALUE)
        )
      );
      const map = new Map();
      reviewedStatuses.forEach((reviewedStatus) => {
        const { studySmid, studyDescription, studyDate } = reviewedStatus;
        const existing = map.get(studySmid);
        map.set(studySmid, {
          smid: studySmid,
          // don't overwrite existing descriptions and dates if defined
          description: studyDescription ?? existing?.description,
          studyDate: studyDate ?? existing?.studyDate,
        });
      });
      return map;
    },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent',
  },
});

export const useIsStackReviewed = (stack?: Stack | null): boolean => {
  const caseSmid = useCurrentCaseId();
  const stackSeriesSmids = useMemo(
    // @ts-expect-error [EN-7967] - TS2339 - Property 'frames' does not exist on type 'Stack'.
    () => [...new Set(stack?.frames?.map((frame: any) => frame.series.smid) ?? [])],
    [stack]
  );
  // @ts-expect-error [EN-7967] - TS2322 - Type 'unknown[]' is not assignable to type 'string[]'.
  return useRecoilValue(isStackReviewed({ caseSmid, stackSeriesSmids }));
};

export const useReviewedStudySmids = (study?: Study | null): Set<string> => {
  const caseSmid = useCurrentCaseId();
  return useRecoilValue(reviewedStudySmids(caseSmid));
};

export const useReviewedStudiesByDate = ({
  context,
}: WithApolloContext): Map<string, ReviewedStudyByDate> => {
  const caseSmid = useCurrentCaseId();
  const { mainStudiesIds } = useBaseViewerData({ context });
  // useRecoilValueLoadable never throws — it surfaces Recoil cache-key failures
  // (which occur when caseSmid is null with cachePolicy_UNSTABLE) as 'hasError'
  // state instead of propagating them through the render tree.
  const loadable = useRecoilValueLoadable(
    reviewedStudiesByDateMap({ caseSmid, currentStudySmids: mainStudiesIds })
  );
  if (loadable.state === 'hasValue') return loadable.contents;
  // Cache-key failures only occur when caseSmid is null; errors with a
  // non-null caseSmid are unexpected and should still surface.
  if (caseSmid == null) return EMPTY_REVIEWED_STUDIES_BY_DATE;
  throw loadable.contents;
};

export const useResetReviewedStudiesByDate = (): (() => void) => {
  const caseSmid = useCurrentCaseId();

  const resetReviewedStatuses = useResetRecoilState(caseReviewedStatuses(caseSmid ?? ''));
  return () => {
    resetReviewedStatuses();
  };
};

export function useTrackViewedSlices({ context }: WithApolloContext): void {
  const viewType = useViewType();
  const viewportId = useViewportId();
  const { imagingProvider } = useImagingContext();
  const caseSmid = useCurrentCaseId();
  const { studies } = useStudies({ context });

  const updateReviewedSeries = useRecoilCallback(
    ({ set }) =>
      (newSeriesSmids: Set<string>) => {
        if (caseSmid != null) {
          set(caseReviewedStatuses(caseSmid), (reviewedSeries) => {
            // @ts-expect-error [EN-10263] - TS7034 - Variable 'newReviewedSeries' implicitly has type 'any[]' in some locations where its type cannot be determined.
            const newReviewedSeries = [];
            newSeriesSmids.forEach((newSeriesSmid) => {
              // for every series that is sufficiently reviewed in a scroll operation,
              // mark it as reviewed only if it hasn't already
              if (reviewedSeries.every((rs) => rs.seriesSmid !== newSeriesSmid)) {
                // for multi-layer stacks, the layer's series might one day come from
                // a different study than the current viewport's base series' study,
                // so we should do a search across all studies
                const study = studies.find((study) =>
                  study.seriesList.some((studySeries) => studySeries.smid === newSeriesSmid)
                );

                if (study == null) {
                  return;
                }

                newReviewedSeries.push({
                  seriesSmid: newSeriesSmid,
                  studySmid: study.smid,
                  studyDate: study.studyDate,
                  studyDescription: study.description,
                  threshold: REVIEWED_THRESHOLD,
                });
              }
            });

            if (newReviewedSeries.length === 0) {
              // all reviewed series are already marked, so don't change anything
              return reviewedSeries;
            }

            // @ts-expect-error [EN-10263] - TS7005 - Variable 'newReviewedSeries' implicitly has an 'any[]' type.
            return [...reviewedSeries, ...newReviewedSeries];
          });
        }
      },
    [caseSmid, studies]
  );

  useEffect(() => {
    if (imagingProvider == null) return;

    const handleActiveSliceChanged = () => {
      const slice = imagingProvider.getActiveSlice(viewType, viewportId);

      const seriesWithThresholdsReached = imagingProvider.markSliceViewed(viewType, slice);
      updateReviewedSeries(seriesWithThresholdsReached);
    };

    // call with current active slice when initialized
    handleActiveSliceChanged();

    imagingProvider.emitter.on(
      createActiveSliceChangedEmitterEvent(viewType),
      handleActiveSliceChanged
    );

    return () => {
      imagingProvider.emitter.off(
        createActiveSliceChangedEmitterEvent(viewType),
        handleActiveSliceChanged
      );
    };
  }, [viewType, viewportId, imagingProvider, updateReviewedSeries]);
}
